import { apiClient } from '../apiClient';

export interface LoginRequest { email: string; password: string; }

export const authApi = {
  login: async (req: LoginRequest) => {
    const { data } = await apiClient.post('/api/v1/auth/login', req);
    return data as { access_token: string; expires_in: number };
  },
  refresh: async () => {
    const { data } = await apiClient.post('/api/v1/auth/refresh');
    return data as { access_token: string; expires_in: number };
  },
  logout: async () => {
    await apiClient.post('/api/v1/auth/logout');
  },
  setup: async (req: { token: string; username: string; password: string }) => {
    const { data } = await apiClient.post('/api/v1/auth/setup', req);
    return data as { access_token: string; expires_in: number };
  },
  forgotPassword: async (email: string) => {
    await apiClient.post('/api/v1/auth/forgot-password', { email });
  },
  resetPassword: async (req: { token: string; password: string }) => {
    await apiClient.post('/api/v1/auth/reset-password', req);
  },
};

export interface MeProfile {
  id: string;
  username: string;
  email: string;
  role: string;
  org_id?: string;
  avatar_url?: string;
  bio?: string;
  headline?: string;
  specializations?: string[];
  links?: Record<string, string>;
}

export const getMe = async (): Promise<MeProfile> => {
  const { data } = await apiClient.get<MeProfile>('/api/v1/auth/me');
  return data;
};

export interface UpdateProfileRequest {
  username?: string;
  bio?: string;
  avatar_url?: string;
  headline?: string;
  specializations?: string[];
  links?: Record<string, string>;
}

export interface ProfileUpdateResult {
  access_token: string;
  expires_in: number;
  user: MeProfile;
}

export const updateProfile = async (
  req: UpdateProfileRequest,
): Promise<ProfileUpdateResult> => {
  const { data } = await apiClient.patch('/api/v1/auth/me', req);
  return data;
};

// uploadAvatar sends the chosen image as multipart/form-data. The server stores
// it and returns a fresh access token (avatar_url is a JWT claim).
export const uploadAvatar = async (file: File): Promise<ProfileUpdateResult> => {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post('/api/v1/auth/me/avatar', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const removeAvatar = async (): Promise<ProfileUpdateResult> => {
  const { data } = await apiClient.delete('/api/v1/auth/me/avatar');
  return data;
};

// Resume (student uploads a PDF; org admins of the student can view it).
export const uploadResume = async (file: File): Promise<{ has_resume: boolean }> => {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post('/api/v1/auth/me/resume', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const getResumeStatus = async (): Promise<{ has_resume: boolean }> => {
  const { data } = await apiClient.get('/api/v1/auth/me/resume/status');
  return data;
};

// Fetches a user's resume PDF (authorized: owner / super_admin / org admin of the
// student) and returns an object URL to open. Throws on 404 (no resume).
export const openResume = async (userId: string): Promise<string> => {
  const { data } = await apiClient.get(`/api/v1/auth/resume/${userId}`, { responseType: 'blob' });
  return URL.createObjectURL(data as Blob);
};

export const changePassword = async (currentPassword: string, newPassword: string): Promise<{ status: string }> => {
  const { data } = await apiClient.post<{ status: string }>('/api/v1/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return data;
};

export interface InviteRow {
  id: string;
  email: string;
  org_id?: string;
  role: string;
  expires_at: string;
}

export const inviteApi = {
  list: async () => {
    const { data } = await apiClient.get('/api/v1/invites');
    return (data ?? []) as InviteRow[];
  },
  create: async (req: { email: string; role: string }) => {
    const { data } = await apiClient.post('/api/v1/invites', req);
    return data as { invite_url: string; expires_at: string };
  },
  revoke: async (id: string) => {
    await apiClient.delete(`/api/v1/invites/${id}`);
  },
  previewSetup: async (token: string) => {
    const { data } = await apiClient.get('/api/v1/auth/setup/preview', { params: { token } });
    return data as { email: string; role: string; org_name?: string; expires_at: string };
  },
  startSetupOAuth: async (req: { token: string; provider: 'google' | 'github' }) => {
    const { data } = await apiClient.post('/api/v1/auth/setup-oauth/start', req);
    return data as { redirect_url: string };
  },
};
